import {Exclude, Expose, instanceToPlain, plainToInstance, Type} from "class-transformer";
import * as t from "io-ts";
import "reflect-metadata";
import {validateJSON} from "../../web/validation";
import {TransportAssocArrayValidator, TransportAssocEntity} from "../transport-assoc";
import {PipelineFlowTransportArrayValidator, PipelineFlowTransportEntity} from "../pipeline/pipeline-flow-transport";

export const RawStatisticsValidator = t.intersection([
    t.type({}),
    t.partial({
        ttaList: TransportAssocArrayValidator,
        flowTransports: PipelineFlowTransportArrayValidator,
        uom: t.string,
        startHourTime: t.number,
        startDayTime: t.number,
        startWeekTime: t.number,
        startMonthTime: t.number,
        defaultMonthList: t.array(t.number),
        companyName: t.string,
        companyType: t.string,
    }),
]);

export type RawStatistics = t.TypeOf<typeof RawStatisticsValidator>;

/**
 * Raw insights.
 */
@Exclude()
export class RawStatisticsEntity {
    @Expose({name: 'ttaList'})
    @Type(() => TransportAssocEntity)
    ttas: TransportAssocEntity[] = [];

    @Expose({name: 'flowTransports'})
    @Type(() => PipelineFlowTransportEntity)
    flows: PipelineFlowTransportEntity[] = [];

    @Expose()
    uom?: string;

    @Expose()
    startHourTime?: number;

    @Expose()
    startDayTime?: number;

    @Expose()
    startWeekTime?: number;

    @Expose()
    startMonthTime?: number;

    @Expose()
    defaultMonthList?: number[];

    @Expose()
    companyName?: string;

    @Expose()
    companyType?: string;

    /**
     * Constructor for RawStatisticsEntity.
     * @hideconstructor
     */
    constructor() {
        // Empty constructor for 'class-transformer'
    }

    /**
     * Create a RawStatisticsEntity from a RawStatistics type value.
     *
     * @param stat The raw insights type value.
     */
    static from(stat: RawStatistics): RawStatisticsEntity {
        return plainToInstance(RawStatisticsEntity, stat, {
            excludeExtraneousValues: true,
        });
    }

    /**
     * Deserializes the RawStatisticsEntity to a RawStatistics type value.
     * If validation fails, the promise will be rejected.
     */
    toTypeValue(): Promise<RawStatistics> {
        const object = instanceToPlain(this, {
            excludeExtraneousValues: true,
        });
        return validateJSON(RawStatisticsValidator, object);
    }
}
